import React, { useMemo, useRef, useEffect, useLayoutEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'
import format from 'date-fns/format'
import { Layout } from '../Layout'
import { SaveBar } from './RuleEditorPanels'
import { FloatingToolbar } from './FloatingToolbar'
import { PromotionDetailDummy } from './PromotionDetailDummy'
import { Tooltip } from '../ui/tooltip'
import { SetItems } from '../../types/promotions'
import { SmartCampaignBackButton } from './SmartCampaignBackButton'
import PromotionI = Models.Promotion
import PromotionItemI = Models.PromotionItemI
import PromotionRuleI = Models.PromotionRuleI

// ── Props ─────────────────────────────────────────────────────────────────────

export interface PromotionEditorNewProps {
    promotionId: string | undefined
    promotion: PromotionI
    items: PromotionItemI[]
    rules: PromotionRuleI[]
    setItems: (items: SetItems) => any
    setRules: any
    screenshotMode: boolean
    dirty: boolean
    saving: boolean
    loading?: boolean
    isNew: boolean
    hasActiveSubscription: boolean
    /** Result of the shared client-side preflight in pages/promotion.tsx */
    validation: { isValid: boolean; reason?: string }
    onSave: () => void
    onCancel: () => void
    onSwitchToClassic?: () => void
    setPromotionValue: <K extends keyof PromotionI>(key: K) => (value: PromotionI[K]) => void
}

// ── Main component ────────────────────────────────────────────────────────────

export const PromotionEditorNew = ({
    promotionId,
    promotion,
    items,
    rules,
    setItems,
    setRules,
    screenshotMode,
    dirty,
    saving,
    loading = false,
    isNew,
    hasActiveSubscription,
    validation,
    onSave,
    onCancel,
    onSwitchToClassic,
    setPromotionValue,
}: PromotionEditorNewProps) => {
    const canSave = hasActiveSubscription && validation.isValid
    const titlePlaceholder = isNew ? 'New Smart Campaign' : 'Campaign name'
    const titleTextForWidth = (promotion.name || titlePlaceholder).trimEnd() || titlePlaceholder
    const titleMeasureRef = useRef<HTMLSpanElement>(null)
    const [titleWidth, setTitleWidth] = useState<number | undefined>()
    useLayoutEffect(() => {
        const measure = titleMeasureRef.current
        if (!measure) return
        setTitleWidth(Math.ceil(measure.getBoundingClientRect().width) + 12)
    }, [titleTextForWidth])
    let disabledReason: string | undefined
    if (!hasActiveSubscription) {
        disabledReason = 'An active subscription is required to save changes'
    } else if (!validation.isValid) {
        disabledReason = validation.reason
    }
    // ── Keyboard shortcuts: always-on, work even before the SaveBar mounts ─────
    const onSaveRef = useRef(onSave)
    const onCancelRef = useRef(onCancel)
    const shortcutStateRef = useRef({ dirty, saving, hasActiveSubscription })
    useEffect(() => {
        onSaveRef.current = onSave
    })
    useEffect(() => {
        onCancelRef.current = onCancel
    })
    useEffect(() => {
        shortcutStateRef.current = { dirty, saving, hasActiveSubscription }
    }, [dirty, saving, hasActiveSubscription])
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const isSaveShortcut =
                (e.metaKey || e.ctrlKey) && (e.key?.toLowerCase() === 's' || e.code === 'KeyS')

            if (!isSaveShortcut) {
                return
            }

            e.preventDefault()
            e.stopPropagation()
            e.stopImmediatePropagation()

            const state = shortcutStateRef.current
            console.info('[SmartCampaign] save shortcut captured', {
                page: 'campaign-detail',
                key: e.key,
                code: e.code,
                metaKey: e.metaKey,
                ctrlKey: e.ctrlKey,
                ...state,
            })
            if (state.dirty && !state.saving && state.hasActiveSubscription) {
                onSaveRef.current()
            } else {
                console.info('[SmartCampaign] save shortcut ignored', {
                    page: 'campaign-detail',
                    reason: !state.dirty
                        ? 'not dirty'
                        : state.saving
                          ? 'already saving'
                          : 'inactive subscription',
                })
            }
        }
        window.addEventListener('keydown', handler, { capture: true })
        return () => window.removeEventListener('keydown', handler, { capture: true })
    }, [])
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !saving) onCancelRef.current()
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [saving])

    const activeRuleCount = useMemo(() => rules.filter((r) => r.enabled).length, [rules])

    /** "January 5, 2025 at 11:33 PM" */
    const fmtFull = (iso: string | null | undefined): string | null => {
        if (!iso) return null
        try {
            return format(new Date(iso), "MMMM d, yyyy 'at' h:mm a")
        } catch {
            return null
        }
    }

    const updatedAgo = useMemo(() => {
        if (!promotion.updated_at) return null
        try {
            return formatDistanceToNow(new Date(promotion.updated_at), { addSuffix: true })
        } catch {
            return null
        }
    }, [promotion.updated_at])

    const lastChangeAgo = useMemo(() => {
        if (!promotion.last_change) return null
        try {
            return formatDistanceToNow(new Date(promotion.last_change), { addSuffix: true })
        } catch {
            return null
        }
    }, [promotion.last_change])

    const lastRunAgo = useMemo(() => {
        if (!promotion.last_run) return null
        try {
            return formatDistanceToNow(new Date(promotion.last_run), { addSuffix: true })
        } catch {
            return null
        }
    }, [promotion.last_run])

    const timestampMetadata = [
        updatedAgo && {
            key: 'updated',
            label: `Updated ${updatedAgo}`,
            tooltip: fmtFull(promotion.updated_at),
        },
        lastChangeAgo && {
            key: 'last-change',
            label: `Last change ${lastChangeAgo}`,
            tooltip: fmtFull(promotion.last_change),
        },
        lastRunAgo && {
            key: 'last-run',
            label: `Last run ${lastRunAgo}`,
            tooltip: fmtFull(promotion.last_run),
        },
    ].filter((item): item is { key: string; label: string; tooltip: string } => !!item?.tooltip)

    return (
        <>
            <Layout
                breadcrumbs={[
                    { text: 'Automations' },
                    { href: '/smart-campaigns', text: 'Smart Campaigns' },
                ]}
                primary={isNew ? 'New Smart Campaign' : promotion.name || 'Smart Campaign'}
                layoutClassName="px-3 md:px-6 pb-6"
            >
                <FloatingToolbar onSwitchToClassic={onSwitchToClassic} />

                <div className={`max-w-3xl mx-auto${dirty ? ' pb-16' : ''}`}>
                    {/* Back + title hero */}
                    <div className="pt-16 mb-8">
                        <div className="mb-2">
                            <SmartCampaignBackButton href="/smart-campaigns" />
                        </div>
                        {loading ? (
                            <div className="h-6 w-full max-w-[460px] rounded-lg bg-[#E7ECF2] animate-pulse" />
                        ) : (
                            <div className="relative inline-block max-w-full">
                                <span
                                    ref={titleMeasureRef}
                                    aria-hidden="true"
                                    className="pointer-events-none invisible absolute left-0 top-0 whitespace-pre rounded-lg border border-transparent px-1 py-0.5 text-[24px] font-[700] tracking-[-0.03em] leading-none"
                                >
                                    {titleTextForWidth}
                                </span>
                                <input
                                    type="text"
                                    value={promotion.name ?? ''}
                                    onChange={(e) =>
                                        setPromotionValue('name')(e.target.value as any)
                                    }
                                    placeholder={titlePlaceholder}
                                    style={{ width: titleWidth, maxWidth: '100%' }}
                                    className="block rounded-lg bg-transparent border border-transparent outline-none text-[24px] font-[700] tracking-[-0.03em] leading-none text-[#242529] placeholder:text-[#CDD6DF] focus:ring-2 focus:ring-[#3391FF]/20 focus:border-[#3391FF] hover:bg-[#F8FAFC] px-1 py-0.5 -ml-1 transition-colors truncate"
                                />
                            </div>
                        )}
                        {/* Metadata row — only shown for saved campaigns */}
                        {!isNew &&
                            (loading ? (
                                <div className="mt-4 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <div className="h-4 w-28 rounded bg-[#E7ECF2] animate-pulse" />
                                        <div className="h-4 w-24 rounded bg-[#E7ECF2] animate-pulse" />
                                        <div className="h-4 w-28 rounded bg-[#E7ECF2] animate-pulse" />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="h-4 w-32 rounded bg-[#E7ECF2] animate-pulse" />
                                        <div className="h-4 w-24 rounded bg-[#E7ECF2] animate-pulse" />
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-3 space-y-1 text-[14px] text-[#94A3B8] select-none">
                                    {timestampMetadata.length > 0 && (
                                        <div className="flex items-center gap-2">
                                            {timestampMetadata.map((item, index) => (
                                                <React.Fragment key={item.key}>
                                                    {index > 0 && (
                                                        <span className="text-[#DBE2E8]">·</span>
                                                    )}
                                                    <Tooltip
                                                        content={item.tooltip}
                                                        position="bottom"
                                                    >
                                                        <span className="cursor-default hover:text-[#7289A3] transition-colors">
                                                            {item.label}
                                                        </span>
                                                    </Tooltip>
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <span>
                                            {items.length} linked ad group
                                            {items.length !== 1 ? 's' : ''}
                                        </span>
                                        <span className="text-[#DBE2E8]">·</span>
                                        <span>
                                            {activeRuleCount} active rule
                                            {activeRuleCount !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                </div>
                            ))}
                    </div>

                    <PromotionDetailDummy
                        screenshotMode={screenshotMode}
                        items={items}
                        setItems={setItems}
                        rules={rules}
                        setRules={setRules}
                        useNewItemsTable
                        loading={loading}
                    />
                </div>
            </Layout>

            {dirty && (
                <SaveBar
                    onSave={onSave}
                    onCancel={onCancel}
                    isSaving={saving}
                    isNew={isNew}
                    isValid={canSave}
                    disabledReason={disabledReason}
                    saveLabel={isNew ? 'Create Smart Campaign' : 'Save changes'}
                />
            )}
        </>
    )
}
